import { Navigate, Routes, Route, useParams } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { CircularProgress, Container, Stack } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import AppTopBar from '@/components/app-top-bar/AppTopBar'
import ProjectTables from '@/components/project-tables/ProjectTables'
import FieldConfiguration from '@/components/project-settings/FieldConfiguration'
import ListsPage from './ListsPage'
import NotFound from './NotFound'
import OrganizationsPage from './OrganizationsPage'
import ProjectLayout from '@/components/project-tables/ProjectLayout'
import ProjectsPage from './ProjectsPage'
import ProjectSettings from '@/components/project-settings/ProjectSettings'
import ProjectSettingsLayout from '@/components/project-settings/ProjectSettingsLayout'
import Source from '@/components/project-settings/Source'
import GlobalSettingsPage from './GlobalSettingsPage'
import TeamsPage from './TeamsPage'
import UsersPage from './UsersPage'
import FeatureFlagsPage from './FeatureFlagsPage'
import ProjectWizardPage from '@/components/project-wizard/ProjectWizardPage'
import BreadcrumbPortalProvider from '@/components/breadcrumbs/BreadcrumbPortalProvider'
import NavigationRailPortalProvider from '@/components/navigation-rail/NavigationRailPortalProvider'
import DrawerPortalProvider from '@/components/drawer/DrawerPortalProvider'
import AppLayout from '@/components/app-layout/AppLayout'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import TeamsTable from '@/components/teams/TeamsTable'
import TeamPage from '@/components/teams/TeamPage'
import DataListList from '@/components/data-lists/DataListList'
import DataListPage from '@/components/data-lists/DataListPage'
import DocumentsProcessed from '@/components/data-visualization/DocumentsProcessed'
import UsersTable from '@/components/users/UsersTable'
import UserPage from '@/components/users/UserPage'
import AgreementsPage from './AgreementsPage'
import OpenSourcePage from './OpenSourcePage'
import MetricsPage from './MetricsPage'
import ProfilePage from './ProfilePage'
import TermsOfUsePage from './TermsOfUsePage'
import VerifyEmailPage from './VerifyEmailPage'
import CredentialsPage from '@/components/credentials-page/CredentialsPage'
import CredentialAuthenticatedPage from '@/components/credentials-page/CredentialAuthenticatedPage'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import ProjectTablesRouting from '@/components/project-tables/ProjectTablesRouting'
import TopBarPortalProvider from '@/components/top-bar-portal/TopBarPortalProvider'
import WorkflowStatesPage from '@/components/workflow-states-page/WorkflowStatesPage'
import ModelSettings from '@/components/model-page/ModelConfiguration'
import DocumentRulesPage from '@/components//project-settings/document-rules/DocumentRulesPage'
import FieldRulesPage from '@/components/project-settings/field-rules/FieldRulesPage'
import TotalDocumentsPage from '@/components/total-documents-page/TotalDocumentsPage'
import WorkflowSettings from '@/components/project-settings/WorkflowSettings'
import CustomVariablesPage from './CustomVariablesPage'
import OrganizationTypesPage from './OrganizationTypesPage'
import ModelLibrary from '@/components/model-library/ModelLibrary'
import ModelPage from '@/components/model-library/ModelPage'
import ModelsTablePage from '@/components/project-settings/models/ModelsTablePage'
import PreferencesPage from './PreferencesPage'
import TagsPage from '@/components/tags-page/TagsPage'
import DashboardRouting from '@/components/dashboard-routing/DashboardRouting'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import usePermission from '@/hooks/usePermission'
import IntegrationSettings from '@/components/project-settings/IntegrationSettings'

const AdvancedSettings = lazy(
  () =>
    import('@/components/project-settings/advanced-settings/AdvancedSettings'),
)
const ZerapixManagementPage = lazy(
  () => import('@/components/zerapix-management/ZerapixManagementPage'),
)
const CustomersPage = lazy(
  () => import('@/components/zerapix-management/CustomersPage'),
)
const CustomerPage = lazy(
  () => import('@/components/zerapix-management/CustomerPage'),
)

function OldValidationRedirect() {
  const { projectId, documentId } = useParams()
  return (
    <Navigate
      to={`/projects/${projectId}/documents?document_id=${documentId}`}
      replace
    />
  )
}

export default function Application() {
  const featureFlags = useFeatureFlagContext()
  const [demoMode] = useDemoModeContext()
  const isSuperUser = useIsSuperUser()
  const [projectPreferredTab] = useLocalStorage(
    'project-preferred-tab',
    'documents',
  )
  const { rootOrganization } = useRootOrganization()
  const { hasEditingPermission } = usePermission()

  return (
    <NavigationRailPortalProvider>
      <DrawerPortalProvider>
        <TopBarPortalProvider>
          <BreadcrumbPortalProvider>
            <AppLayout header={<AppTopBar />}>
              <Suspense
                fallback={
                  <Stack alignItems="center" sx={{ p: 4 }}>
                    <CircularProgress />
                  </Stack>
                }
              >
                <Routes>
                  <Route path="projects" element={<ProjectsPage />} />

                  <Route
                    path="project-setup/*"
                    element={<ProjectWizardPage />}
                  />

                  <Route path="projects/:projectId" element={<ProjectLayout />}>
                    {/* If no sub-view is given, send them to "projectPreferredTab" */}
                    <Route
                      index
                      element={<Navigate to={projectPreferredTab} replace />}
                    />

                    <Route path="dashboard" element={<DashboardRouting />} />

                    <Route
                      path="validation/:documentId"
                      element={<OldValidationRedirect />}
                    />

                    <Route path=":view" element={<ProjectTablesRouting />}>
                      <Route index element={<ProjectTables />} />
                    </Route>

                    <Route path="settings" element={<ProjectSettingsLayout />}>
                      <Route
                        index
                        element={<Navigate to="general" replace />}
                      />
                      <Route path="general" element={<ProjectSettings />} />

                      {featureFlags.model_library && (
                        <Route
                          path="model-library/:id"
                          element={<ModelPage />}
                        />
                      )}

                      <Route path="sources" element={<Source />} />
                      <Route path="fields" element={<FieldConfiguration />} />
                      {featureFlags.model_library && (
                        <Route
                          path="model-library"
                          element={<ModelLibrary />}
                        />
                      )}

                      {!demoMode && isSuperUser && (
                        <Route
                          path="model-tables"
                          element={<ModelsTablePage />}
                        />
                      )}

                      <Route path="tags" element={<TagsPage />} />
                      {!demoMode && isSuperUser && (
                        <Route
                          path="advanced-settings"
                          element={<AdvancedSettings />}
                        />
                      )}

                      <Route
                        path="document-rules"
                        element={<DocumentRulesPage />}
                      />
                      <Route path="field-rules" element={<FieldRulesPage />} />

                      <Route
                        path="workflow-general"
                        element={<WorkflowSettings />}
                      />

                      <Route
                        path="workflow-states/*"
                        element={<WorkflowStatesPage />}
                      />

                      <Route
                        path="model-configuration"
                        element={<ModelSettings />}
                      />

                      <Route
                        path="integrations/:integrationId"
                        element={<IntegrationSettings />}
                      />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Route>

                  <Route path="settings" element={<GlobalSettingsPage />}>
                    <Route
                      index
                      element={<Navigate to="my-profile" replace />}
                    />

                    <Route path="my-profile" element={<ProfilePage />} />
                    <Route
                      path="my-preferences"
                      element={<PreferencesPage />}
                    />

                    <Route path="teams" element={<TeamsPage />}>
                      <Route index element={<TeamsTable />} />
                      <Route path=":id" element={<TeamPage />} />
                    </Route>

                    <Route path="users" element={<UsersPage />}>
                      <Route index element={<UsersTable />} />
                      <Route path=":id" element={<UserPage />} />
                    </Route>

                    <Route
                      path="organizations/*"
                      element={<OrganizationsPage />}
                    />

                    <Route
                      path="organization-types"
                      element={<OrganizationTypesPage />}
                    />

                    <Route
                      path="custom-variables/*"
                      element={<CustomVariablesPage />}
                    />

                    <Route path="lists" element={<ListsPage />}>
                      <Route index element={<DataListList />} />
                      <Route path=":id" element={<DataListPage />} />
                    </Route>

                    {featureFlags.customer_page_visible && (
                      <Route path="customer/:id" element={<CustomerPage />} />
                    )}
                    {hasEditingPermission('edit_orgs', rootOrganization.id) && (
                      <Route path="credentials" element={<CredentialsPage />} />
                    )}
                  </Route>

                  <Route
                    path="credential-authenticated"
                    element={<CredentialAuthenticatedPage />}
                  />

                  <Route path="agreements" element={<AgreementsPage />} />
                  <Route
                    path="open-source-licenses"
                    element={<OpenSourcePage />}
                  />
                  <Route path="terms-of-use" element={<TermsOfUsePage />} />

                  <Route path="verify-email" element={<VerifyEmailPage />} />

                  <Route path="metrics" element={<MetricsPage />}>
                    <Route index element={<Navigate to="documents" />} />
                    <Route
                      path="documents"
                      element={
                        <Container sx={{ py: 4 }}>
                          <DocumentsProcessed />
                        </Container>
                      }
                    />
                  </Route>

                  {isSuperUser && (
                    <Route
                      path="zerapix-management"
                      element={<ZerapixManagementPage />}
                    >
                      <Route index element={<Navigate to="customers" />} />
                      <Route path="customers" element={<CustomersPage />} />
                      <Route
                        path="customers/:id"
                        element={<CustomerPage showBackButton />}
                      />
                      <Route
                        path="feature-flags"
                        element={<FeatureFlagsPage />}
                      />
                    </Route>
                  )}

                  {isSuperUser && (
                    <Route
                      path="total-documents"
                      element={<TotalDocumentsPage />}
                    />
                  )}

                  <Route
                    path="/"
                    element={<Navigate to="projects" replace />}
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AppLayout>
          </BreadcrumbPortalProvider>
        </TopBarPortalProvider>
      </DrawerPortalProvider>
    </NavigationRailPortalProvider>
  )
}
